import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode
} from "react";
import { cn } from "@/lib/cn.js";

/**
 * Headless dropdown menu — click to toggle, click-outside to close, escape to close.
 * Modeled after the Radix DropdownMenu API surface but without the Portal/positioning
 * machinery (we anchor with relative + absolute, which is enough for v1's needs).
 */

interface DropdownContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

export function Dropdown({ children }: { children: ReactNode }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <DropdownContext.Provider value={{ open, setOpen, containerRef }}>
      <div ref={containerRef} className="relative inline-block text-left">
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

function useDropdown(): DropdownContextValue {
  const ctx = useContext(DropdownContext);
  if (!ctx) throw new Error("Dropdown primitives must be used within <Dropdown>");
  return ctx;
}

export function DropdownTrigger({ children, className, ...props }: HTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  const { open, setOpen } = useDropdown();
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={open}
      className={cn(className)}
      onClick={(event) => {
        props.onClick?.(event);
        setOpen(!open);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

export function DropdownContent({ children, className, align = "end", ...props }: HTMLAttributes<HTMLDivElement> & { align?: "start" | "end" }): React.JSX.Element | null {
  const { open } = useDropdown();
  if (!open) return null;
  return (
    <div
      role="menu"
      className={cn(
        "absolute z-30 mt-1.5 min-w-[12rem] rounded-md border bg-popover p-1 text-popover-foreground shadow-md animate-slide-down",
        align === "end" ? "right-0" : "left-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function DropdownItem({ children, className, onSelect, ...props }: HTMLAttributes<HTMLDivElement> & { onSelect?: () => void }): React.JSX.Element {
  const { setOpen } = useDropdown();
  return (
    <div
      role="menuitem"
      tabIndex={0}
      className={cn(
        "relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2.5 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
        className
      )}
      onClick={(event) => {
        props.onClick?.(event);
        onSelect?.();
        setOpen(false);
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export function DropdownSeparator(): React.JSX.Element {
  return <div className="my-1 h-px bg-muted" />;
}
